import React from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronUp, Code2 } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useDevMode } from '../context/DevModeContext';
import { useTeam } from '../context/TeamContext';
import { useTheme } from '../hooks/useTheme';
import { ThemeToggleIcon, themeToggleLabel } from './ThemeToggle';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Where to land after switching organisation: the top of the area the user was
 * in. A record page (a quote, a support request) belongs to the organisation
 * being left, so staying on it would open something the new one cannot see.
 */
export const areaRoot = (pathname) => `/${pathname.split('/')[1] || 'dashboard'}`;

/**
 * The signed-in user's menu at the foot of the sidebar. Expanded, the trigger
 * shows the user's initials and name; collapsed, the initials alone.
 */
const UserMenu = ({ collapsed = false }) => {
  const { user, logout } = useAuth();
  const { isDev, enabled: devModeEnabled, toggle: toggleDevMode } = useDevMode();
  const { setSelectedTeamId } = useTeam();
  const { darkMode, setDarkMode } = useTheme();
  const location = useLocation();

  const displayName = user?.name || `${user?.first_name} ${user?.last_name}`;
  const organisationName = user?.organisation?.name || user?.organisationName;
  const organisations = user?.teams || [];

  const initials = () => {
    if (!user?.first_name || !user?.last_name) return 'U';
    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
  };

  const avatar = (
    <div className="h-8 w-8 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
      {initials()}
    </div>
  );

  // Same as picking an organisation after sign in: store the choice, then a
  // full reload so every context and subscription starts again on the new one.
  const switchOrganisation = (teamId) => {
    if (teamId === user?.teamId) return;
    setSelectedTeamId(teamId);
    window.location.assign(areaRoot(location.pathname));
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {collapsed ? (
          <Button
            variant="ghost"
            className="w-full h-11 justify-center px-2"
            aria-label="Account menu"
            title={displayName}
          >
            {avatar}
          </Button>
        ) : (
          <Button
            variant="ghost"
            className="w-full justify-start h-auto p-3 hover:bg-accent"
          >
            <div className="flex items-center space-x-3 w-full">
              {avatar}
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium truncate">{displayName}</p>
              </div>
              <ChevronUp className="ml-auto" />
            </div>
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={collapsed ? 'right' : 'top'}
        align={collapsed ? 'end' : 'start'}
        className="w-72"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuLabel className="font-normal">
          <p className="text-sm font-medium truncate">{displayName}</p>
          {organisationName && (
            <p className="text-xs text-muted-foreground truncate">{organisationName}</p>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {/* Stays open on select, so the change can be seen and undone. */}
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            setDarkMode(!darkMode);
          }}
          aria-label={`Theme: ${themeToggleLabel(darkMode).toLowerCase()}`}
        >
          <span>Theme</span>
          <span className="ml-auto">
            <ThemeToggleIcon darkMode={darkMode} />
          </span>
        </DropdownMenuItem>
        {isDev && (
          <div className="flex items-center justify-between px-2 py-1.5">
            <div className="flex items-center gap-2 text-sm">
              <Code2 className="h-4 w-4" />
              <span>Dev mode</span>
            </div>
            <Switch
              checked={devModeEnabled}
              onCheckedChange={toggleDevMode}
            />
          </div>
        )}
        {organisations.length > 1 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Switch organisation</DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-56 max-h-80 overflow-y-auto">
              <DropdownMenuRadioGroup value={user?.teamId} onValueChange={switchOrganisation}>
                {organisations.map((organisation) => (
                  <DropdownMenuRadioItem key={organisation.id} value={organisation.id}>
                    <span className="truncate">{organisation.name}</span>
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={logout}
          className="text-destructive focus:text-destructive"
        >
          <span>Log Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
